import { useEffect, useRef } from 'react'

import { createDeferredPromise } from '../../utils/async'

/**
 * The {@link useRegisterReload} hook publishes the component's `reload`
 * callback to a {@link useReloadRegistry} registry while the component is
 * mounted, so an owner outside of it can refresh the list it renders.
 *
 * @usage
 * ```
 *  useRegisterReload({ isReady: isInitiated, reload: handleReload, onRegisterReload })
 * ```
 *
 * @note
 * A reload requested while the component is not ready (a table whose initial
 * load is still in flight — that response may predate the action) is deferred
 * and runs once `isReady` turns true; the returned promise settles with it.
 * The owner therefore treats a mounted-but-loading tab like a loaded one.
 * The promise adopts the reload's outcome, and a table reload that fails never
 * settles (`useAsyncCallback`): the profile owners never await a tab's list —
 * `reloadRegistryTabsAndCounters` returns the counters promise only.
 *
 * @param {Object} params
 * @param {boolean} [params.isReady=true]
 * @param {Function} params.reload
 * @param {Function} [params.onRegisterReload]
 */
export function useRegisterReload({
  isReady = true,
  reload,
  onRegisterReload,
}) {
  const deferredReloadRef = useRef(null)

  useEffect(() => {
    if (isReady && deferredReloadRef.current) {
      const { params, resolve } = deferredReloadRef.current

      deferredReloadRef.current = null
      resolve(reload(params))
    }

    async function handleReload(params) {
      if (isReady) {
        return reload(params)
      }

      if (!deferredReloadRef.current) {
        deferredReloadRef.current = { ...createDeferredPromise(), params }
      }

      return deferredReloadRef.current.promise
    }

    onRegisterReload?.(handleReload)

    return () => {
      onRegisterReload?.(null)
    }
  }, [isReady, onRegisterReload, reload])

  useEffect(() => {
    return () => {
      // A reload still deferred at unmount can no longer run — settle it so
      // the callers awaiting it do not hang.
      deferredReloadRef.current?.resolve()
    }
  }, [])
}
